import { useMemo } from 'react';
import type { FPIRow } from '../../../../studio-blocks/types';
import { compact, get, isNil } from 'lodash';
import type { ColDef } from 'ag-grid-community';
import { createLabelColumn } from '../../../../studio-blocks/logic/columnParsers';
import { getComparisonLabelForBlockLegend } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import {
  getChangesColumnDef,
  getDefaultCellClass,
  getDefaultValueFormat,
} from '../../../../studio-blocks/logic/gridStyling';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { useSelectedMetrics } from '../../../../studio-blocks/logic/useSelectedMetrics';
import type { FinancialPerformanceIndicatorsResponse } from 'venn-api';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees, showBlockChangesColumn } from 'venn-state';

export const useFinancialPerformanceIndicatorsTable = (id: string, data: FinancialPerformanceIndicatorsResponse) => {
  const selectedMetrics = useSelectedMetrics();
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(id));
  const showChangesColumn = useRecoilValue(showBlockChangesColumn(id));

  const rowData = useMemo(
    (): FPIRow[] =>
      selectedMetrics.map((metric) => {
        const value = data.contributions
          .map((c) => c.value)
          .map((v) => v.metadata.financialPerformanceIndicators)
          .map((fpiData) => get(fpiData, metric.metricPath!));
        const changes =
          value.length === 2 && metric.dataType !== 'TEXT' && !isNil(value[0]) && !isNil(value[1])
            ? value[0] - value[1]
            : undefined;
        return {
          key: metric.key,
          label: metric.label,
          type: metric.dataType,
          value,
          changes,
        };
      }),
    [selectedMetrics, data],
  );

  const columnDefs = useMemo(
    () =>
      compact([
        createLabelColumn({}),
        ...allSubjects.map(
          (subject, index): ColDef<FPIRow> => ({
            headerName: getComparisonLabelForBlockLegend(subject),
            headerComponent: HeaderCellRenderer,
            headerComponentParams: {
              subject,
            },
            valueGetter: ({ data }) => data?.value[index],
            valueFormatter: getDefaultValueFormat,
            cellRenderer: ValueCellRenderer,
            cellClass: getDefaultCellClass,
            autoHeight: false,
          }),
        ),
        showChangesColumn ? getChangesColumnDef() : undefined,
      ]),
    [allSubjects, showChangesColumn],
  );

  return { rowData, columnDefs };
};
